"use client";

import { cn } from "@/lib/utils";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useState,
} from "react";

type CollapsibleContextValue = {
  open: boolean;
  animated: boolean;
};

const CollapsibleContext = createContext<CollapsibleContextValue | null>(null);

type CollapsibleProps = ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root> & {
  animated?: boolean;
};

const Collapsible = forwardRef<ElementRef<typeof CollapsiblePrimitive.Root>, CollapsibleProps>(
  ({ children, open: controlledOpen, onOpenChange, animated = true, ...props }, ref) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(props.defaultOpen ?? false);

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : uncontrolledOpen;

    const handleOpenChange = (newOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    };

    return (
      <CollapsibleContext.Provider value={{ open, animated }}>
        <CollapsiblePrimitive.Root ref={ref} open={open} onOpenChange={handleOpenChange} {...props}>
          {children}
        </CollapsiblePrimitive.Root>
      </CollapsibleContext.Provider>
    );
  },
);
Collapsible.displayName = "Collapsible";

const useCollapsible = () => {
  const context = useContext(CollapsibleContext);
  if (!context) {
    throw new Error("useCollapsible must be used within a Collapsible");
  }
  return context;
};

type CollapsibleTriggerProps = Omit<
  ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleTrigger>,
  "children"
> & {
  children: ((open: boolean) => React.ReactNode) | React.ReactNode;
};

const CollapsibleTrigger = forwardRef<
  ElementRef<typeof CollapsiblePrimitive.CollapsibleTrigger>,
  CollapsibleTriggerProps
>(({ children, ...props }, ref) => {
  const { open } = useCollapsible();

  const renderChildren = () => {
    if (typeof children === "function") {
      return children(open);
    }
    if (isValidElement(children)) {
      return children;
    }
    return children;
  };

  return (
    <CollapsiblePrimitive.CollapsibleTrigger ref={ref} {...props}>
      {renderChildren()}
    </CollapsiblePrimitive.CollapsibleTrigger>
  );
});
CollapsibleTrigger.displayName = "CollapsibleTrigger";

const CollapsibleContent = forwardRef<
  ElementRef<typeof CollapsiblePrimitive.CollapsibleContent>,
  ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(({ className, ...props }, ref) => {
  const { animated } = useCollapsible();

  return (
    <CollapsiblePrimitive.CollapsibleContent
      ref={ref}
      className={cn(
        "overflow-hidden",
        animated && "data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up",
        className,
      )}
      {...props}
    />
  );
});
CollapsibleContent.displayName = "CollapsibleContent";

export { Collapsible, CollapsibleTrigger, CollapsibleContent };
